import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { heatColors, useChartTheme } from './chart-theme'
import { calendarDays, countedZone, periodDays, selectedText, weekdayOf } from './calendar-days'
import { zoneName } from '../../date/local-time'
import { HeatScale, heatColor, heatKind, heatPieces } from './heat-steps'
import { dayText, NO_RANGE, plural, useInsight } from './panel'
import './insights.scss'

const ROW_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', '']
const monthLabel = day => new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })
// Columns are weeks and rows are weekdays, as drawn.
const STEP = { ArrowUp: -1, ArrowDown: 1, ArrowLeft: -7, ArrowRight: 7 }

// The calendar strip above the timeline (mockup .calstrip): the last 12
// months, a day per cell, in the same heat steps and colours as the
// Insights calendar, drawn with CSS so the timeline loads no chart
// library. It reads the calendar with the timeline's params (its own
// 12-month window) and a day calls onPickDay(YYYY-MM-DD, { from, to,
// timeZone }: the day as the server counted it, in the reader's zone, and
// the window the strip counted, so the page opens that day's local
// midnights, or only the part of an edge day it counted). Phones
// scroll it sideways, newest month in view. One cell is in the tab order;
// the arrow keys move between days.
// range: the page's range (rangeView), whose days are outlined.
export const CalendarStrip = ({ params, range = NO_RANGE, onPickDay }) => {
  const ref = useRef(null)
  const T = useChartTheme(ref)
  const state = useInsight('calendar', params, { navigator: true, fallback: "The calendar couldn't be loaded." })
  const days = useMemo(() => calendarDays(state.data), [state.data])
  const tz = countedZone(state.data)
  const kind = heatKind(params)
  const colors = useMemo(() => heatColors(T, kind), [T, kind])
  const pieces = useMemo(() => heatPieces(days.map(d => d.total), colors), [days, colors])
  const period = useMemo(() => periodDays(params, days, tz), [params, days, tz])
  const inPeriod = useMemo(() => new Set(period ? period.days : []), [period])
  const [focus, setFocus] = useState(-1)
  const current = focus >= 0 && focus < days.length ? focus : days.length - 1
  const grid = useRef(null)
  const scroller = useRef(null)
  useLayoutEffect(() => {
    if (scroller.current) scroller.current.scrollLeft = scroller.current.scrollWidth
  }, [days])

  const first = days.length ? weekdayOf(days[0].day) : 0
  const months = []
  days.forEach((d, i) => {
    if (i === 0 || d.day.endsWith('-01')) months.push({ key: d.day, text: monthLabel(d.day), col: Math.floor((first + i) / 7) + 1 })
  })
  const total = days.reduce((a, d) => a + d.total, 0)

  const onKeyDown = e => {
    const step = STEP[e.key]
    if (!step) return
    const i = Number(e.target.dataset && e.target.dataset.i)
    const next = i + step
    if (Number.isNaN(i) || next < 0 || next >= days.length) return
    e.preventDefault()
    setFocus(next)
    const cell = grid.current && grid.current.querySelector(`[data-i="${next}"]`)
    if (cell) cell.focus()
  }

  return (
    <section className="au-calstrip" aria-label="Activity over the last 12 months" ref={ref}>
      {state.error && <p className="sw-hint m-0">{state.error}</p>}
      {days.length > 0 &&
        <>
          <div className="au-keyrow">
            <span>{`${plural(total, 'entry', 'entries')} in 12 months`}</span>
            <span className="au-cal-selkey"><i className="au-sw au-ring au-period-key" aria-hidden="true" />{`Selected: ${selectedText(range)}`}</span>
            <HeatScale colors={colors} />
          </div>
          <div className="au-cal">
            <div className="au-cal-rows" aria-hidden="true">{ROW_LABELS.map((l, i) => <span key={i}>{l}</span>)}</div>
            <div className="au-hscroll" ref={scroller}>
              <div className="au-cal-months" aria-hidden="true">
                {months.map(m => <span key={m.key} style={{ gridColumnStart: m.col }}>{m.text}</span>)}
              </div>
              <div className="au-cal-grid" ref={grid} role="group" aria-label="Days" onKeyDown={onKeyDown}>
                {days.map((d, i) => {
                  const text = `${dayText(d.day)}: ${d.total ? plural(d.total, 'entry', 'entries') : 'no entries'}`
                  return (
                    <button
                      key={d.day}
                      type="button"
                      className={`au-cal-cell${inPeriod.has(d.day) ? ' au-cal-in' : ''}`}
                      data-i={i}
                      tabIndex={i === current ? 0 : -1}
                      title={text}
                      aria-label={text}
                      style={{ background: heatColor(d.total, pieces), gridRowStart: i === 0 ? first + 1 : undefined }}
                      onFocus={() => setFocus(i)}
                      onClick={() => onPickDay(d.day, { from: state.data.from, to: state.data.to, timeZone: tz })}
                    />
                  )
                })}
              </div>
            </div>
          </div>
          <p className="sw-hint au-note">{`Click a day to show only that day. Days are in ${zoneName(tz)}.`}</p>
        </>}
      {state.data && !days.length && <p className="sw-hint m-0">No entries in the last 12 months.</p>}
    </section>
  )
}
